import { useEffect, useState } from "react";
import { OutOfCreditsKa } from "./out-of-credits-ka";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AICreditDisplay } from "@/components/folder/ai-credits";
import {
  formatDurationRemainingToCreditsRenewal,
  useUserAndCredits,
} from "@/components/utils/credits";
import { CoinsIcon, LockKeyholeIcon, XIcon } from "lucide-react";
import { Reward } from "@/components/rewarding-system-v2/reward-manager";
import { LightningBoltIcon } from "@radix-ui/react-icons";
import { Link, useLocation, useSearchParams } from "react-router";
import { usePostHog } from "posthog-js/react";
import { Duration } from "date-fns";
import { DotLottieReact } from "@lottiefiles/dotlottie-react";
import { useRewardManager } from "@/components/rewarding-system-v2/reward-manager";
import { maybeGetDeckNameFromPath } from "./upgrade-dialog";

const REWARDS_FORMATS_V2 = {
  "study-flashcard": {
    title: "Study to get more credits!",
    description:
      "You've run out of credits, but there's a simple solution:\n start a study session and you'll earn an extra 15 credits.",
    lottieUrl:
      "https://lottie.host/83fff7f4-ae07-485e-9ad9-de026ba5441f/CrC5MTTy9V.lottie",
  },
  "do-quiz": {
    title: "Do a Quiz to get more credits!",
    description:
      "You've run out of credits, but there's a simple solution:\n  Test your knowledge with Multiple Choice Quizzes.",
    lottieUrl:
      "https://lottie.host/02ad726a-86ba-40b6-aa06-b180962b2ae6/nponNVA4oa.lottie",
  },
  "share-deck": {
    title: "Share your Deck to get more credits!",
    description:
      "You've run out of credits, but there's a simple solution: Share your flashcards to your study buddies. ",
    lottieUrl:
      "https://lottie.host/080e1933-f534-4e3e-b468-3368d4d5f028/2S3O0pmdUP.lottie",
  },
  "highlight-generate": {
    title: "Highlight from pdf to get more credits!",
    description:
      "You've run out of credits, but there's a simple solution: Use the Highlight mode in the PDF view and generate 1 Flashcard.",
    lottieUrl:
      "https://lottie.host/77f2f592-b163-4720-a24a-3aa3ccba18df/RUvkjIhTDW.lottie",
  },
  "use-ai-helps": {
    title: "Use AI Helps to get more credits!",
    description:
      "You've run out of credits, but there's a simple solution: Use the Explain, Memo, Example or Visualise in the study mode!",
    lottieUrl:
      "https://lottie.host/e268a056-8bec-4ddf-ba0a-94fdc6407393/waV9Xkf6Fm.lottie",
  },
};

const noRewardText = (
  <>
    Come back <b>tomorrow</b> or <b>upgrade</b> your plan
  </>
);

export const OutOfCreditsDialog = ({
  isOpen,
  setIsOpen,
  onRewardTaskAccept,
  onUpgradePressed,
  onWaitPressed,
  showAmbassadorDialog,
  showDiscountDialog,
  currentDeckName,
}: {
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
  onRewardTaskAccept?: (reward: Reward) => void;
  onUpgradePressed?: () => void;
  onWaitPressed?: () => void;
  showAmbassadorDialog: boolean;
  showDiscountDialog: boolean;
  currentDeckName?: string;
}) => {
  const posthog = usePostHog();
  const { pathname } = useLocation();
  const [searchParams] = useSearchParams();

  const { timeToNextRenewal } = useUserAndCredits();

  const { getNextReward, acceptRewardTask } = useRewardManager();

  let nextRewardV2;

  nextRewardV2 = getNextReward?.() ?? undefined;

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(isOpen) => {
        // Don't allow the closing option from this dialog but from the X below
        if (showAmbassadorDialog) {
          return;
        }

        if (!isOpen) {
          setIsOpen(false);
        }
      }}
    >
      <DialogContent
        className="max-w-[90vw] md:max-w-2xl lg:max-w-3xl overflow-y-auto"
        closeButton={
          showAmbassadorDialog ? (
            <Button
              variant="strokeSecondary"
              size="iconMd"
              onClick={() => {
                posthog.capture(
                  "Dialog-Out-Of-Credits-Ambassador-Close-Clicked"
                );
                setIsOpen(false);
              }}
            >
              <XIcon />
            </Button>
          ) : undefined
        }
      >
        {showDiscountDialog ? (
          <div>
            <DiscountDialog
              clickOnDecline={() => {
                posthog.capture(
                  "Dialog-Out-Of-Credits-Discount-Decline-Clicked"
                );
                setIsOpen(false);
              }}
              clickOnUpgrade={() => {
                posthog.capture(
                  "Dialog-Out-Of-Credits-Discount-Upgrade-Clicked"
                );
                onUpgradePressed?.();
              }}
            />
          </div>
        ) : showAmbassadorDialog ? (
          <div>
            <Ambassador
              clickOnUpgrade={() => {
                posthog.capture(
                  "Dialog-Out-Of-Credits-Ambassador-Upgrade-Clicked"
                );
                onUpgradePressed?.();
              }}
            />
          </div>
        ) : !nextRewardV2 ? (
          <OutOfCreditsNoReward
            timeToNextRenewal={timeToNextRenewal}
            onWaitPressed={onWaitPressed}
            onUpgradePressed={onUpgradePressed}
          />
        ) : (
          <OutOfCreditsWithRewards
            nextReward={nextRewardV2}
            onRewardTaskAccept={(reward) => {
              acceptRewardTask?.(
                reward,
                currentDeckName ??
                  maybeGetDeckNameFromPath(pathname, searchParams) ??
                  ""
              );
              onRewardTaskAccept?.(reward);
            }}
            onUpgradePressed={onUpgradePressed}
          />
        )}
      </DialogContent>
    </Dialog>
  );
};

const OutOfCreditsNoReward = ({
  timeToNextRenewal,
  onWaitPressed,
  onUpgradePressed,
}: {
  timeToNextRenewal: Duration | undefined;
  onWaitPressed?: () => void;
  onUpgradePressed?: () => void;
}) => {
  const [showIncreaseBar, setShowIncreaseBar] = useState(false);

  useEffect(() => {
    if (!showIncreaseBar) {
      const timeout = setTimeout(() => {
        setShowIncreaseBar(true);
      }, 1500);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [showIncreaseBar, setShowIncreaseBar]);

  return (
    <div>
      <div className="flex flex-col items-center justify-center gap-6">
        <OutOfCreditsKa />
        <div className="flex flex-col items-center justify-center py-6 gap-4">
          <h2 className="inline text-title-950 text-[28px] font-bold font-['Mona Sans']">
            Oh no... You are out of AI Credits
          </h2>
          <span className="text-body-600 text-base font-medium font-['Mona Sans'] break-words">
            {noRewardText}
          </span>
        </div>
        <div className="flex flex-col-reverse md:flex-row gap-3 w-full items-end">
          <Button
            variant="secondary"
            className="w-full flex-1 px-4 py-2"
            onClick={() => {
              onWaitPressed?.();
            }}
          >
            <LockKeyholeIcon className="h-5 w-5 mr-2" /> Wait
            <b className="ml-1">
              {formatDurationRemainingToCreditsRenewal(
                timeToNextRenewal ?? {},
                2
              )}
            </b>
          </Button>
          <Button
            variant="secondary"
            className="w-full flex-1 px-4 py-2"
            onClick={() => {
              onUpgradePressed?.();
            }}
          >
            <LightningBoltIcon className="h-5 w-5 mr-2" /> Upgrade
          </Button>
        </div>
      </div>
    </div>
  );
};

const OutOfCreditsWithRewards = ({
  nextReward,
  onUpgradePressed,
  onRewardTaskAccept,
}: {
  nextReward: { reward: Reward; credits: number };
  onUpgradePressed?: () => void;
  onRewardTaskAccept?: (reward: Reward) => void;
}) => {
  const [showIncreaseBar, setShowIncreaseBar] = useState(false);
  // Since Reward is now a string with a brand, we can use it directly as a key
  const rewardData = REWARDS_FORMATS_V2[nextReward.reward];

  useEffect(() => {
    if (!showIncreaseBar) {
      const timeout = setTimeout(() => {
        setShowIncreaseBar(true);
      }, 1500);

      return () => {
        clearTimeout(timeout);
      };
    }
  }, [showIncreaseBar, setShowIncreaseBar]);

  return (
    <div className="flex flex-col items-center justify-center gap-1">
      <DotLottieReact src={rewardData.lottieUrl} loop autoplay />
      <div className="flex flex-col items-center justify-center py-6 gap-4">
        <h2 className="inline text-title-950 text-[28px] font-bold font-['Mona Sans']">
          {rewardData.title}
        </h2>
        <span className="text-body-600 text-base text-center font-medium font-['Mona Sans'] break-words">
          {rewardData.description}
        </span>
      </div>
      <AICreditDisplay
        isCollapsed={false}
        isShowWhenPremium={false}
        showUpgradeButton={false}
        showWaitTime={false}
        creditsIncrease={showIncreaseBar ? nextReward.credits : 0}
      />
      <div className="flex flex-col-reverse md:flex-row gap-3 w-full items-end">
        <Button
          variant="primary"
          size="lg"
          className="flex-1"
          onClick={() => {
            onRewardTaskAccept?.(nextReward.reward);
          }}
        >
          <CoinsIcon />
          <span>
            Earn <b>+{nextReward.credits} Credits</b>
          </span>
        </Button>
        <Button
          variant="secondary"
          size="lg"
          className="w-full flex-1"
          onClick={() => {
            onUpgradePressed?.();
          }}
        >
          <LightningBoltIcon className="h-5 w-5" />
          <span>
            Upgrade to <b>Premium</b>
          </span>
        </Button>
      </div>
    </div>
  );
};

const Ambassador = ({ clickOnUpgrade }: { clickOnUpgrade: () => void }) => {
  const posthog = usePostHog();
  return (
    <div className="flex flex-col items-center justify-center gap-6">
      <img
        src="/images/ambassador.png"
        width={451}
        height={244}
        alt="ambassador program"
      />
      <div className="flex flex-col items-center justify-center py-6 gap-4 text-center">
        <h2 className="inline text-secondary-950 text-[28px] font-bold font-['Mona Sans']">
          Want Premium Access?
        </h2>
        <span className="text-secondary-600 text-base text-center">
          Become a <span className="font-medium">Flashka Ambassador!</span>
          <br />
          Support us 5 minutes a day to gain Flashka Wizard Plan
          <br />
          and special rewards!
        </span>
      </div>
      <div className="flex flex-col-reverse md:flex-row gap-3 w-full items-end">
        <Button
          variant="secondary"
          className="w-full flex-1 px-4 py-2"
          onClick={() => {
            clickOnUpgrade();
          }}
        >
          <LightningBoltIcon className="h-5 w-5 mr-2" /> Upgrade
        </Button>
        <Link
          to="https://flashka.notion.site/Want-Flashka-For-free-18f13db82562806cb417cc99d79a82d6"
          target="_blank"
          rel="noopener noreferrer"
          className="w-full flex-1"
          onClick={() => {
            posthog.capture(
              "Dialog-Out-Of-Credits-Ambassador-Free-Premium-Clicked"
            );
          }}
        >
          <Button variant="primary" className="w-full px-4 py-2">
            <CoinsIcon className="h-5 w-5 mr-2" /> Get Premium for FREE
          </Button>
        </Link>
      </div>
    </div>
  );
};

const DiscountDialog = ({
  clickOnDecline,
  clickOnUpgrade,
}: {
  clickOnDecline: () => void;
  clickOnUpgrade: () => void;
}) => {
  const posthog = usePostHog();
  return (
    <div className="flex flex-col items-center justify-center gap-6">
      <DotLottieReact
        src="https://lottie.host/d33a1d4d-8003-4b82-b1db-eab559a4ee5a/DrXAhQBDxR.lottie"
        autoplay={true}
        loop={true}
        style={{ width: "100%", height: "100%" }}
        className="-mb-6"
      />
      <div className="flex flex-col items-center justify-center py-6 gap-4 text-center">
        <h2 className="inline text-secondary-950 text-[28px] font-bold font-['Mona Sans']">
          Get 50% Off now!
        </h2>
        <span className="text-secondary-600 text-base text-center">
          You’ve unlocked a personal promo! 💜
          <br />
          Choose a premium plan,{" "}
          <span className="font-medium">
            apply the code 50SPECIAL2 at checkout
          </span>
          ,<br /> and enjoy your discount!
        </span>
      </div>
      <div className="flex flex-col-reverse md:flex-row gap-3 w-full items-end">
        <Button
          variant="secondary"
          className="w-full flex-1 px-4 py-2"
          onClick={() => {
            clickOnDecline();
          }}
        >
          <XIcon className="h-5 w-5 mr-1" /> Decline Offer
        </Button>
        <Button
          variant="primary"
          className="w-full flex-1 px-4 py-2"
          onClick={() => {
            posthog.capture("Dialog-Out-Of-Credits-Discount-Premium-Clicked");
            clickOnUpgrade();
          }}
        >
          <LightningBoltIcon className="h-5 w-5 mr-1" /> Get 50% Off with code
          <b>50SPECIAL2</b>
        </Button>
      </div>
    </div>
  );
};
